/*
 * « Nouveau lead » : encodage manuel (fiche complète) ou envoi du formulaire d'adhésion.
 * L'envoi crée le lead avec les champs exigés au stade Lead (réglables dans Paramètres),
 * puis envoie le lien vers le formulaire « nous-rejoindre », pré-rempli avec l'e-mail.
 * Quand la personne remplit le formulaire avec cette adresse, le lead est complété automatiquement.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Building2, Loader2, PenLine, Send, User } from 'lucide-react';
import type { CrmContact, CrmContactDraft, EntityType, MessageTemplate, Meter, Role, SharingOperation, VatRegime } from './types';
import { ROLE_LABELS, VAT_LABELS, emptyMeter, DEFAULT_ANNUAL_FEE, feeAfterEntityChange } from './types';
import { type DefaultPrices, fetchDefaultPrices, pricesAfterChange } from './defaultPrices';
import {
  type FieldKey, REQUIREMENT_RULES, fieldLabel, formatErrors, isEmpty, missingFields, normalizeDraft, requirementStage,
} from './validation';
import { createContact, fetchTemplates, renderTemplate, sendQuestionnaire } from './api';
import { Field, ModalShell, borderFor, inputClass, errorMessage } from './ui';

interface Props {
  /** Contacts déjà chargés, pour repérer une adresse e-mail déjà connue. */
  contacts: CrmContact[];
  operations: SharingOperation[];
  onCancel: () => void;
  onManual: () => void;
  /** Lead créé ou mis à jour (formulaire envoyé ou non). */
  onDone: (c: CrmContact, openDrawer: boolean) => void;
}

/** Règles affichées à part (choix du profil, nom, e-mail). */
const BUILT_IN = new Set(['role', 'entity_type', 'name', 'email']);
const INPUT_TYPES: Record<string, string> = {
  phone: 'tel', billing_email: 'email', birth_date: 'date', price_cents_kwh: 'number', sell_price_cents_kwh: 'number', annual_fee: 'number',
};
const NUMERIC = new Set(['price_cents_kwh', 'sell_price_cents_kwh', 'annual_fee']);
const FORM_URL = 'https://www.sunisup.be/nous-rejoindre';

export function NewLeadDialog({ contacts, operations, onCancel, onManual, onDone }: Props) {
  const [mode, setMode] = useState<'choose' | 'form'>('choose');
  const [draft, setDraft] = useState<CrmContactDraft>({ entity_type: 'physique', role: null, source: 'manuel', meters: [emptyMeter()], annual_fee: DEFAULT_ANNUAL_FEE.physique });
  const [template, setTemplate] = useState<MessageTemplate | null>(null);
  const [sending, setSending] = useState(false);
  const [tried, setTried] = useState(false);
  /* Tarifs par défaut (Paramètres) : appliqués quand le type de membre ou de personne est choisi. */
  const [defaultPrices, setDefaultPrices] = useState<DefaultPrices | null>(null);
  useEffect(() => { fetchDefaultPrices().then(setDefaultPrices).catch(() => setDefaultPrices(null)); }, []);
  const withDefaultPrices = (prev: CrmContactDraft, next: CrmContactDraft): CrmContactDraft => ({ ...next, ...pricesAfterChange(defaultPrices, prev, next) });

  useEffect(() => {
    if (mode !== 'form' || template) return;
    fetchTemplates().then(list => setTemplate(list.find(t => t.key === 'questionnaire') ?? null)).catch(() => {});
  }, [mode, template]);

  const set = (key: FieldKey, value: unknown) => setDraft(prev => ({ ...prev, [key]: value }));
  const meter: Meter = draft.meters?.[0] ?? emptyMeter();
  const setMeter = (patch: Partial<Meter>) => setDraft(prev => ({ ...prev, meters: [{ ...(prev.meters?.[0] ?? emptyMeter()), ...patch }] }));
  const isPM = draft.entity_type === 'morale';

  /** Champs exigés au stade Lead (Paramètres), en plus du profil, du nom et de l'e-mail. */
  const leadRules = REQUIREMENT_RULES.filter(r => !BUILT_IN.has(r.id) && requirementStage(r) === 'lead' && r.applies(draft));
  const firstNameRequired = leadRules.some(r => r.id === 'first_name');

  const checked: CrmContactDraft = useMemo(() => normalizeDraft({
    ...draft,
    meters: (draft.meters ?? []).filter(m => !isEmpty(m.ean_code) || !isEmpty(m.meter_address) || !isEmpty(m.name)),
  }), [draft]);
  const missing = useMemo(() => {
    const list = missingFields(checked, 'lead');
    if (isEmpty(checked.email) && !list.some(m => m.key === 'email')) list.push({ key: 'email', label: 'E-mail' });
    return list;
  }, [checked]);
  const missingKeys = new Set(missing.map(m => m.key));
  const errors = formatErrors(checked);
  const show = (key: FieldKey) => tried && missingKeys.has(key);
  const existing = !isEmpty(checked.email)
    ? contacts.find(c => (c.email ?? '').toLowerCase() === String(checked.email).toLowerCase())
    : undefined;

  const preview = template && renderTemplate(template, { ...(checked as CrmContact), meters: checked.meters ?? [] }, operations);
  const link = `${FORM_URL}?email=${encodeURIComponent(String(checked.email ?? ''))}`;

  const send = async (target: 'new' | 'existing') => {
    setTried(true);
    if (target === 'new') {
      if (missing.length > 0) { toast.error(`À compléter : ${missing.map(m => m.label).join(', ')}`); return; }
      if (Object.keys(errors).length > 0) { toast.error(Object.values(errors)[0]); return; }
    }
    setSending(true);
    let lead: CrmContact | null = target === 'existing' ? existing ?? null : null;
    try {
      if (!lead) lead = await createContact(checked);
      const sent = await sendQuestionnaire(lead, operations);
      toast.success(`Formulaire d’adhésion envoyé à ${sent.email}`);
      onDone(sent, false);
    } catch (err) {
      const message = errorMessage(err);
      if (lead) {
        toast.error(`Lead enregistré, mais l’e-mail n’est pas parti : ${message}`);
        onDone(lead, true);
      } else {
        toast.error(`Erreur : ${message}`);
      }
    } finally {
      setSending(false);
    }
  };

  const textField = (key: FieldKey, opts: { required?: boolean; label?: string; full?: boolean; placeholder?: string } = {}) => (
    <Field key={key} label={opts.label ?? fieldLabel(key, draft.entity_type, draft.role)} required={opts.required}
      missing={show(key)} error={errors[key]} className={opts.full ? 'col-span-2' : ''}>
      <input
        type={INPUT_TYPES[key] ?? 'text'}
        step={NUMERIC.has(key) ? 'any' : undefined}
        value={String((draft as Record<string, unknown>)[key] ?? '')}
        placeholder={opts.placeholder}
        onChange={e => set(key, e.target.value === '' ? null : NUMERIC.has(key) ? Number(e.target.value) : e.target.value)}
        className={`${inputClass} ${borderFor(show(key), errors[key])}`}
      />
    </Field>
  );

  const ruleInput = (id: string) => {
    if (id === 'first_name') return null; // affiché avec le nom
    if (id === 'vat_regime') {
      return (
        <Field key={id} label={fieldLabel('vat_regime', draft.entity_type, draft.role)} required missing={show('vat_regime')} className="col-span-2">
          <select value={draft.vat_regime ?? ''} onChange={e => set('vat_regime', (e.target.value || null) as VatRegime | null)}
            className={`${inputClass} ${borderFor(show('vat_regime'))}`}>
            <option value="">Sélectionner…</option>
            {(Object.keys(VAT_LABELS) as VatRegime[]).map(v => <option key={v} value={v}>{VAT_LABELS[v]}</option>)}
          </select>
        </Field>
      );
    }
    if (id === 'meters') {
      return [
        <Field key="ean" label="Code EAN" required missing={show('meters') || show('meters.0.ean_code')} error={errors['meters.0.ean_code']}>
          <input value={meter.ean_code} onChange={e => setMeter({ ean_code: e.target.value })} placeholder="54xxxxxxxxxxxxxxxx"
            className={`${inputClass} font-mono text-[13px] ${borderFor(show('meters') || show('meters.0.ean_code'), errors['meters.0.ean_code'])}`} />
        </Field>,
        <Field key="addr" label="Adresse du compteur" required missing={show('meters') || show('meters.0.meter_address')}>
          <input value={meter.meter_address ?? ''} onChange={e => setMeter({ meter_address: e.target.value || null })}
            className={`${inputClass} ${borderFor(show('meters') || show('meters.0.meter_address'))}`} />
        </Field>,
      ];
    }
    if (id === 'meter_names') {
      return (
        <Field key={id} label="Nom du participant (compteur 1)" required missing={show('meters.0.name')} className="col-span-2">
          <input value={meter.name ?? ''} onChange={e => setMeter({ name: e.target.value || null })}
            className={`${inputClass} ${borderFor(show('meters.0.name'))}`} />
        </Field>
      );
    }
    const rule = REQUIREMENT_RULES.find(r => r.id === id)!;
    const key = rule.field(draft);
    return textField(key, { required: true, full: key === 'domicile_address' || key === 'billing_address' });
  };

  if (mode === 'choose') {
    const card = 'flex items-start gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#F8BD3D] hover:bg-amber-50/40 text-left transition-colors';
    return (
      <ModalShell title="Nouveau lead" subtitle="Comment voulez-vous l’ajouter ?" onClose={onCancel}>
        <div className="grid gap-3">
          <button type="button" onClick={onManual} className={card}>
            <span className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0"><PenLine className="w-4 h-4 text-neutral-600" /></span>
            <span>
              <span className="block text-sm font-semibold text-neutral-900">Encodage manuel</span>
              <span className="block text-xs text-neutral-500 mt-0.5">Vous remplissez vous-même la fiche du lead.</span>
            </span>
          </button>
          <button type="button" onClick={() => setMode('form')} className={card}>
            <span className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><Send className="w-4 h-4 text-amber-700" /></span>
            <span>
              <span className="block text-sm font-semibold text-neutral-900">Envoyer le formulaire d’adhésion</span>
              <span className="block text-xs text-neutral-500 mt-0.5">
                Vous indiquez l’essentiel, le lead est créé et la personne reçoit le lien du formulaire. Ses réponses complètent le lead automatiquement.
              </span>
            </span>
          </button>
        </div>
      </ModalShell>
    );
  }

  const seg = (active: boolean, warn = false) =>
    `flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold border ${active ? 'border-[#F8BD3D] bg-amber-50 text-neutral-900' : `text-neutral-500 hover:border-neutral-300 ${warn ? 'border-amber-400' : 'border-neutral-200'}`}`;

  return (
    <ModalShell
      title="Envoyer le formulaire d’adhésion"
      subtitle="Le lead est créé, puis la personne reçoit le lien du formulaire"
      onClose={onCancel}
      width="max-w-lg"
      footer={<>
        <button onClick={() => setMode('choose')} className="px-4 py-2 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-600 hover:bg-neutral-100">Retour</button>
        <button onClick={() => send('new')} disabled={sending || !!existing}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 text-sm font-semibold disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Créer le lead et envoyer
        </button>
      </>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {(['physique', 'morale'] as EntityType[]).map(e => (
            <button key={e} type="button" onClick={() => setDraft(prev => withDefaultPrices(prev, { ...prev, entity_type: e, annual_fee: feeAfterEntityChange(prev.annual_fee, prev.entity_type, e) }))} className={seg(draft.entity_type === e)}>
              {e === 'physique' ? <User className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
              {e === 'physique' ? 'Personne physique' : 'Personne morale'}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {(Object.keys(ROLE_LABELS) as Role[]).map(r => (
            <button key={r} type="button" onClick={() => setDraft(prev => withDefaultPrices(prev, { ...prev, role: r }))} className={seg(draft.role === r, show('role'))}>{ROLE_LABELS[r]}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {isPM
            ? textField('company_name', { required: true, full: true, placeholder: 'Dénomination de la société' })
            : <>
                {textField('first_name', { required: firstNameRequired })}
                {textField('last_name', { required: true })}
              </>}
          {textField('email', { required: true, full: true, placeholder: 'nom@exemple.be' })}
          {leadRules.map(r => ruleInput(r.id))}
        </div>

        {existing && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <p>Cette adresse est déjà celle d’un contact du CRM. Le formulaire complétera ce contact.</p>
            <button onClick={() => send('existing')} disabled={sending} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-900 font-semibold hover:bg-amber-100 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" />Envoyer le formulaire à ce contact
            </button>
          </div>
        )}

        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-1.5">
          <p className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">E-mail envoyé</p>
          {preview ? (
            <>
              <p className="text-xs font-semibold text-neutral-800">{preview.subject}</p>
              <p className="text-xs text-neutral-600 whitespace-pre-line">{preview.body.replace(/<[^>]+>/g, '')}</p>
              <p className="text-[11px] text-neutral-500 break-all">Bouton « Remplir le formulaire » : {link}</p>
            </>
          ) : <p className="text-xs text-neutral-400">Chargement du message…</p>}
          <p className="text-[11px] text-neutral-400">Le texte se modifie dans CRM → Paramètres → Messages. Les champs exigés au stade Lead aussi, dans Champs obligatoires.</p>
        </div>
      </div>
    </ModalShell>
  );
}
